import React, { useEffect, useMemo, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Plus, MoreVertical, Trash2, Pencil, X, Phone, Mail, User as UserIcon,
  Building2, Tag, DollarSign, ClipboardList, Check,
} from "lucide-react";
import api from "../../lib/api";

/**
 * Bitrix-style CRM Leads Kanban.
 *  • Columns (stages) are user-managed inline: add, rename, delete, recolor
 *  • Cards drag between columns → PATCH /crm/leads/{id}/move
 *  • Card click opens right slide-over pane with full detail edit
 */

const CURRENCY_SYMBOL = { TRY: "₺", USD: "$", EUR: "€", AED: "د.إ", SAR: "﷼" };

/* ==================================================================
 * Stage header — inline rename / delete / color
 * ================================================================== */
const StageHeader = ({ stage, count, sum, onSave, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(stage.name);
  const [color, setColor] = useState(stage.color || "#8B8D93");
  const [menuOpen, setMenuOpen] = useState(false);

  const save = async () => {
    if (!name.trim()) return;
    await onSave({ name: name.trim(), color });
    setEditing(false);
  };

  return (
    <div
      className="rounded-sm px-3 py-2 flex items-center gap-2 relative"
      style={{ background: `${color}22`, borderTop: `3px solid ${color}` }}
    >
      {editing ? (
        <>
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
            className="flex-1 min-w-0 bg-white/70 border border-black/10 px-2 py-1 text-sm outline-none focus:border-brand-primary"
            data-testid={`stage-name-input-${stage.id}`}
          />
          <input
            type="color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            className="w-6 h-6 border border-black/10 cursor-pointer"
            title="Renk"
          />
          <button onClick={save} className="text-brand-primary hover:text-brand-navy" data-testid={`stage-save-${stage.id}`}>
            <Check size={14} strokeWidth={2} />
          </button>
          <button onClick={() => setEditing(false)} className="text-brand-medium hover:text-brand-navy">
            <X size={14} strokeWidth={2} />
          </button>
        </>
      ) : (
        <>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-brand-navy text-sm truncate">{stage.name}</div>
            <div className="text-[0.62rem] tracking-[0.14em] uppercase text-brand-medium">
              {count} lead {sum > 0 ? `· ${sum.toLocaleString("tr-TR")} ${CURRENCY_SYMBOL[stage.__cur] || ""}` : ""}
            </div>
          </div>
          <button
            type="button"
            className="text-brand-medium hover:text-brand-navy p-1"
            onClick={() => setMenuOpen((v) => !v)}
            data-testid={`stage-menu-${stage.id}`}
          >
            <MoreVertical size={14} strokeWidth={1.5} />
          </button>
          {menuOpen ? (
            <div className="absolute right-2 top-full mt-1 bg-white border border-black/10 shadow-lg z-30 min-w-[160px]">
              <button
                type="button"
                onClick={() => { setEditing(true); setMenuOpen(false); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-brand-light text-left"
                data-testid={`stage-rename-${stage.id}`}
              >
                <Pencil size={12} strokeWidth={1.5} /> Yeniden Adlandır
              </button>
              <button
                type="button"
                onClick={async () => {
                  setMenuOpen(false);
                  if (!window.confirm(`"${stage.name}" kolonunu silmek istediğinizden emin misiniz?`)) return;
                  await onDelete();
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-red-50 text-red-600 text-left"
                data-testid={`stage-delete-${stage.id}`}
              >
                <Trash2 size={12} strokeWidth={1.5} /> Kolonu Sil
              </button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};

/* ==================================================================
 * Lead card
 * ================================================================== */
const LeadCard = ({ lead, index, onOpen }) => (
  <Draggable draggableId={lead.id} index={index}>
    {(prov, snap) => (
      <div
        ref={prov.innerRef}
        {...prov.draggableProps}
        {...prov.dragHandleProps}
        onClick={() => onOpen(lead)}
        className={`bg-white border border-black/5 hover:border-brand-primary p-3 mb-2 cursor-pointer transition-all group ${snap.isDragging ? "shadow-xl border-brand-primary rotate-1" : "hover:shadow-md"}`}
        data-testid={`lead-card-${lead.id}`}
      >
        <div className="font-medium text-sm text-brand-navy truncate">
          {lead.title || lead.contact_name || "İsimsiz Lead"}
        </div>
        {lead.company_name ? (
          <div className="text-xs text-brand-medium mt-0.5 truncate flex items-center gap-1">
            <Building2 size={10} strokeWidth={1.5} /> {lead.company_name}
          </div>
        ) : null}
        <div className="mt-2 flex items-center gap-3 text-[0.7rem] text-brand-medium">
          {lead.phone ? (
            <span className="inline-flex items-center gap-1"><Phone size={10} strokeWidth={1.5} /> {lead.phone}</span>
          ) : null}
          {lead.amount > 0 ? (
            <span className="inline-flex items-center gap-1 text-brand-primary font-medium">
              {lead.amount.toLocaleString("tr-TR")} {CURRENCY_SYMBOL[lead.currency] || lead.currency}
            </span>
          ) : null}
        </div>
        {lead.source ? (
          <div className="mt-2 inline-block text-[0.55rem] tracking-[0.2em] uppercase bg-brand-navy/5 text-brand-navy px-1.5 py-0.5">
            {lead.source}
          </div>
        ) : null}
      </div>
    )}
  </Draggable>
);

/* ==================================================================
 * Slide-over lead editor
 * ================================================================== */
const LeadPane = ({ lead, stages, users, onClose, onSaved, onDeleted, canDelete }) => {
  const isNew = !lead?.id;
  const [form, setForm] = useState({
    title: "",
    contact_name: "",
    company_name: "",
    phone: "",
    email: "",
    stage_id: stages[0]?.id || "",
    source: "",
    amount: 0,
    currency: "TRY",
    responsible_id: "",
    notes: "",
    tags: [],
    ...(lead || {}),
  });
  const [busy, setBusy] = useState(false);
  const [tagInput, setTagInput] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      let saved;
      if (isNew) {
        const r = await api.post("/estateos/crm/leads", form);
        saved = r.data;
      } else {
        const r = await api.put(`/estateos/crm/leads/${lead.id}`, form);
        saved = r.data;
      }
      toast.success("Kaydedildi");
      onSaved(saved);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("Bu lead silinsin mi?")) return;
    setBusy(true);
    try {
      await api.delete(`/estateos/crm/leads/${lead.id}`);
      toast.success("Silindi");
      onDeleted(lead);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <motion.div
      initial={{ x: 480, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: 480, opacity: 0 }}
      transition={{ duration: 0.35, ease: [0.19, 1, 0.22, 1] }}
      className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-white border-l border-black/10 shadow-2xl z-50 flex flex-col"
      data-testid="lead-pane"
    >
      <div className="px-6 py-4 border-b border-black/10 flex items-center justify-between">
        <div>
          <div className="text-[0.6rem] tracking-[0.28em] uppercase text-brand-primary font-medium">
            {isNew ? "Yeni Lead" : "Lead Detayı"}
          </div>
          <div className="font-serif text-lg text-brand-navy mt-0.5 truncate max-w-[280px]">
            {form.title || form.contact_name || "İsimsiz Lead"}
          </div>
        </div>
        <button onClick={onClose} className="text-brand-medium hover:text-brand-navy" data-testid="lead-pane-close">
          <X size={18} strokeWidth={1.5} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-6 space-y-5">
        <Field label="Başlık"><input value={form.title} onChange={(e) => set("title", e.target.value)} className="input" data-testid="lead-title" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Ad Soyad" icon={UserIcon}><input value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} className="input" data-testid="lead-contact" /></Field>
          <Field label="Şirket" icon={Building2}><input value={form.company_name} onChange={(e) => set("company_name", e.target.value)} className="input" /></Field>
          <Field label="Telefon" icon={Phone}><input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="input" data-testid="lead-phone" /></Field>
          <Field label="E-posta" icon={Mail}><input value={form.email} onChange={(e) => set("email", e.target.value)} className="input" /></Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Aşama">
            <select value={form.stage_id} onChange={(e) => set("stage_id", e.target.value)} className="input" data-testid="lead-stage">
              {stages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </Field>
          <Field label="Kaynak"><input value={form.source} onChange={(e) => set("source", e.target.value)} className="input" placeholder="Facebook, Bitrix, ..." /></Field>
          <Field label="Tutar" icon={DollarSign}><input type="number" value={form.amount || 0} onChange={(e) => set("amount", Number(e.target.value))} className="input" /></Field>
          <Field label="Para Birimi">
            <select value={form.currency} onChange={(e) => set("currency", e.target.value)} className="input">
              <option value="TRY">TRY</option><option value="USD">USD</option><option value="EUR">EUR</option><option value="AED">AED</option><option value="SAR">SAR</option>
            </select>
          </Field>
        </div>
        <Field label="Sorumlu">
          <select value={form.responsible_id || ""} onChange={(e) => set("responsible_id", e.target.value)} className="input" data-testid="lead-responsible">
            <option value="">— Seçilmedi —</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.email})</option>)}
          </select>
        </Field>
        <Field label="Etiketler" icon={Tag}>
          <div className="flex flex-wrap gap-1.5 items-center">
            {(form.tags || []).map((t, i) => (
              <span key={i} className="inline-flex items-center gap-1 bg-brand-navy/5 text-brand-navy text-xs px-2 py-0.5">
                {t}
                <button onClick={() => set("tags", form.tags.filter((_, j) => j !== i))} className="text-brand-medium hover:text-red-600"><X size={10} /></button>
              </span>
            ))}
            <input
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && tagInput.trim()) { e.preventDefault(); set("tags", [...(form.tags || []), tagInput.trim()]); setTagInput(""); } }}
              placeholder="+ Etiket"
              className="text-xs bg-transparent outline-none min-w-[80px]"
            />
          </div>
        </Field>
        <Field label="Notlar" icon={ClipboardList}>
          <textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={4} className="input" />
        </Field>
      </div>

      <div className="px-6 py-4 border-t border-black/10 bg-brand-light flex items-center justify-between gap-3">
        {!isNew && canDelete ? (
          <button onClick={remove} disabled={busy} className="text-red-600 hover:text-red-700 text-xs uppercase tracking-[0.2em] inline-flex items-center gap-1" data-testid="lead-delete">
            <Trash2 size={12} strokeWidth={1.5} /> Sil
          </button>
        ) : <span />}
        <div className="flex items-center gap-2">
          <button onClick={onClose} className="px-4 py-2 text-xs uppercase tracking-[0.2em] text-brand-medium hover:text-brand-navy">İptal</button>
          <button onClick={save} disabled={busy} className="btn-primary px-6 py-2" data-testid="lead-save">
            {busy ? "..." : "Kaydet"}
          </button>
        </div>
      </div>
    </motion.div>
  );
};

const Field = ({ label, icon: Icon, children }) => (
  <label className="block">
    <div className="text-[0.6rem] tracking-[0.24em] uppercase text-brand-medium font-medium mb-1.5 flex items-center gap-1">
      {Icon ? <Icon size={10} strokeWidth={1.5} /> : null}
      {label}
    </div>
    {children}
  </label>
);

/* ==================================================================
 * Main page
 * ================================================================== */
const LeadsKanbanPage = () => {
  const { me } = useOutletContext() || {};
  const [stages, setStages] = useState([]);
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [openLead, setOpenLead] = useState(null); // {stage_id?} for new, full obj for edit
  const [addingStage, setAddingStage] = useState(false);
  const [newStageName, setNewStageName] = useState("");
  const loading = useRef(false);

  const canCreate = (me?.permissions?.lead?.create || "none") !== "none";
  const canDelete = (me?.permissions?.lead?.delete || "none") !== "none";
  const canManageStages = me?.role_slug === "yonetici" || me?.role_name === "Yönetici" || !me?.role_slug; // legacy admin

  const load = async () => {
    if (loading.current) return;
    loading.current = true;
    try {
      const [s, l, u] = await Promise.all([
        api.get("/estateos/crm/stages"),
        api.get("/estateos/crm/leads"),
        api.get("/estateos/users").catch(() => ({ data: [] })),
      ]);
      setStages(s.data || []);
      setLeads(l.data || []);
      setUsers(u.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { loading.current = false; }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const leadsByStage = useMemo(() => {
    const m = {};
    stages.forEach((s) => { m[s.id] = []; });
    leads.forEach((l) => {
      if (m[l.stage_id]) m[l.stage_id].push(l);
    });
    Object.values(m).forEach((arr) => arr.sort((a, b) => (a.order || 0) - (b.order || 0)));
    return m;
  }, [stages, leads]);

  const onDragEnd = async (result) => {
    const { destination, source, draggableId } = result;
    if (!destination) return;
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;

    // Optimistic UI
    const moved = leads.find((l) => l.id === draggableId);
    if (!moved) return;
    const newLeads = leads.map((l) => l.id === draggableId ? { ...l, stage_id: destination.droppableId } : l);
    setLeads(newLeads);
    try {
      await api.post(`/estateos/crm/leads/${draggableId}/move`, {
        stage_id: destination.droppableId,
        order: destination.index + 1,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
      load();
    }
  };

  const addStage = async () => {
    if (!newStageName.trim()) return;
    try {
      await api.post("/estateos/crm/stages", { name: newStageName.trim(), entity: "lead" });
      setNewStageName(""); setAddingStage(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };

  const saveStage = async (id, patch) => {
    try { await api.put(`/estateos/crm/stages/${id}`, patch); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };

  const deleteStage = async (id) => {
    try { await api.delete(`/estateos/crm/stages/${id}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };

  return (
    <div className="h-full flex flex-col" data-testid="leads-kanban-page">
      {/* Toolbar */}
      <div className="px-6 md:px-10 py-4 bg-white border-b border-black/5 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[0.62rem] tracking-[0.28em] uppercase text-brand-primary font-medium">CRM</div>
          <h1 className="font-serif text-2xl md:text-3xl text-brand-navy leading-tight">Müşteri Adayları</h1>
        </div>
        <div className="flex items-center gap-2">
          {canCreate && stages.length > 0 ? (
            <button
              onClick={() => setOpenLead({ stage_id: stages[0].id })}
              className="btn-primary px-5 py-2.5 text-xs"
              data-testid="new-lead-btn"
            >
              <Plus size={14} strokeWidth={1.5} /> Yeni Lead
            </button>
          ) : null}
        </div>
      </div>

      {/* Board */}
      <div className="flex-1 min-h-0 overflow-x-auto overflow-y-hidden bg-brand-light">
        <DragDropContext onDragEnd={onDragEnd}>
          <div className="flex gap-3 p-4 md:p-6 h-full">
            {stages.map((s) => {
              const items = leadsByStage[s.id] || [];
              const cur = items[0]?.currency;
              const sum = items.reduce((acc, l) => acc + (Number(l.amount) || 0), 0);
              return (
                <div key={s.id} className="flex flex-col w-[300px] flex-shrink-0" data-testid={`stage-column-${s.id}`}>
                  <StageHeader
                    stage={{ ...s, __cur: cur }}
                    count={items.length}
                    sum={sum}
                    onSave={(patch) => saveStage(s.id, patch)}
                    onDelete={() => deleteStage(s.id)}
                  />
                  <Droppable droppableId={s.id}>
                    {(prov, snap) => (
                      <div
                        ref={prov.innerRef}
                        {...prov.droppableProps}
                        className={`flex-1 min-h-[80px] mt-2 p-2 rounded-sm overflow-y-auto transition-colors ${snap.isDraggingOver ? "bg-brand-navy/5" : ""}`}
                      >
                        {items.map((l, idx) => <LeadCard key={l.id} lead={l} index={idx} onOpen={setOpenLead} />)}
                        {prov.placeholder}
                        {canCreate ? (
                          <button
                            onClick={() => setOpenLead({ stage_id: s.id })}
                            className="w-full text-xs text-brand-medium hover:text-brand-navy border border-dashed border-black/15 hover:border-brand-navy py-2 mt-1 transition-colors"
                            data-testid={`add-lead-in-${s.id}`}
                          >
                            <Plus size={12} strokeWidth={1.5} className="inline mr-1" /> Ekle
                          </button>
                        ) : null}
                      </div>
                    )}
                  </Droppable>
                </div>
              );
            })}

            {/* Add-column card */}
            {canManageStages ? (
              <div className="w-[280px] flex-shrink-0">
                {addingStage ? (
                  <div className="bg-white border border-black/10 p-3">
                    <input
                      autoFocus
                      value={newStageName}
                      onChange={(e) => setNewStageName(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") addStage(); if (e.key === "Escape") setAddingStage(false); }}
                      placeholder="Kolon adı..."
                      className="w-full border border-black/10 px-2 py-1.5 text-sm outline-none focus:border-brand-primary"
                      data-testid="new-stage-input"
                    />
                    <div className="flex items-center gap-2 mt-2">
                      <button onClick={addStage} className="btn-primary px-3 py-1.5 text-xs" data-testid="new-stage-save">Ekle</button>
                      <button onClick={() => { setAddingStage(false); setNewStageName(""); }} className="text-xs text-brand-medium">İptal</button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setAddingStage(true)}
                    className="w-full h-10 border border-dashed border-brand-navy/30 hover:border-brand-navy text-brand-navy text-xs tracking-[0.2em] uppercase inline-flex items-center justify-center gap-2 transition-colors bg-white/50"
                    data-testid="add-stage-btn"
                  >
                    <Plus size={14} strokeWidth={1.5} /> Kolon Ekle
                  </button>
                )}
              </div>
            ) : null}
          </div>
        </DragDropContext>
      </div>

      {/* Slide-over */}
      <AnimatePresence>
        {openLead ? (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/40 z-40"
              onClick={() => setOpenLead(null)}
            />
            <LeadPane
              lead={openLead}
              stages={stages}
              users={users}
              onClose={() => setOpenLead(null)}
              onSaved={(saved) => {
                setLeads((prev) => {
                  const exists = prev.find((x) => x.id === saved.id);
                  if (exists) return prev.map((x) => x.id === saved.id ? saved : x);
                  return [...prev, saved];
                });
                setOpenLead(null);
              }}
              onDeleted={(deleted) => {
                setLeads((prev) => prev.filter((x) => x.id !== deleted.id));
                setOpenLead(null);
              }}
              canDelete={canDelete}
            />
          </>
        ) : null}
      </AnimatePresence>

      {/* input helper style */}
      <style>{`
        .input { width:100%; background:#fff; border:1px solid rgba(0,0,0,.1); padding:0.5rem 0.65rem; font-size:0.85rem; outline:none; }
        .input:focus { border-color:#22346B; }
      `}</style>
    </div>
  );
};

export default LeadsKanbanPage;
